/**
 * WordPress subdirectory link interceptor — v2.0.2 hash-anchor aware.
 *
 * When the React bundle runs inside a WordPress theme installed at a
 * subdirectory (e.g. `https://eureka-bs.com/eureka-staging/`), three things
 * need to be reconciled for navigation to "just work":
 *
 *   1. Plain `<a href="/services">` points at the **domain root**, not the
 *      WP install root. We prepend window.EurekaConfig.basename when needed.
 *   2. SPA navigation should NOT trigger a full page reload — we use
 *      history.pushState + a manual popstate dispatch so React Router takes
 *      over client-side.
 *   3. Hash anchors (`/#assessment`, `/#services`, `/#faq`, …) should scroll
 *      to the matching element id on the page. Browsers do this natively for
 *      real navigations; pushState does NOT, so we replicate the scroll
 *      ourselves after a microtask + a frame so React has time to render any
 *      route change.
 *
 * In dev / standalone preview where EurekaConfig.basename is empty, items 1
 * and 2 are no-ops; item 3 (hash scroll) still runs because hash anchors are
 * meaningful in dev too.
 */
export function installWpLinkIntercept() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__eurekaLinkInterceptInstalled__) return;
  window.__eurekaLinkInterceptInstalled__ = true;

  const getBasename = () => {
    const b = (window.EurekaConfig && window.EurekaConfig.basename) || '';
    if (!b || b === '/') return '';
    return b.replace(/\/+$/, ''); // strip trailing slash
  };

  const scrollToHash = (hash) => {
    if (!hash || hash === '#') return;
    const id = decodeURIComponent(hash.slice(1));
    const el = id ? document.getElementById(id) : null;
    if (!el) {
      // No matching element — at least scroll to top so the route change
      // doesn't leave the user mid-page.
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return; // left-click only
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const a = event.target && event.target.closest && event.target.closest('a');
      if (!a) return;
      // Explicit opt-out: any anchor that ships `data-no-intercept` is treated
      // as "let the browser handle this natively" — useful for any future
      // edge cases beyond the heuristics below.
      if (a.hasAttribute('data-no-intercept')) return;
      const target = a.getAttribute('target');
      if (target && target !== '_self') return; // _blank etc → native
      if (a.hasAttribute('download')) return;

      const href = a.getAttribute('href');
      if (!href) return;
      // Pure hash anchor on the current page → just scroll, no history push.
      if (href.startsWith('#')) {
        event.preventDefault();
        scrollToHash(href);
        return;
      }
      // Allow external (http:, https:, mailto:, tel:, whatsapp:, sms:,
      // intent:, fb-messenger:, instagram:, tg:, ...), and protocol-relative.
      // Anything that starts with "scheme:" or "//" is bypassed.
      if (/^([a-z][a-z0-9+.\-]*:|\/\/)/i.test(href)) return;
      if (!href.startsWith('/')) return;
      // Skip WordPress admin / login / json / uploads / wp-content.
      if (/^\/(wp-admin|wp-login\.php|wp-json|wp-content|wp-includes)/.test(href)) return;
      // Same-origin absolute URL (e.g. a.href === 'https://eureka-bs.com/x'):
      // double-check using URL parsing. If the host differs from window's
      // host, treat as external and bypass.
      try {
        const parsed = new URL(a.href, window.location.href);
        if (parsed.origin !== window.location.origin) return;
      } catch (_e) { /* fall through */ }

      const basename = getBasename();
      // If a basename is configured and the href doesn't already begin with
      // it, prepend it (so /services becomes /eureka-staging/services).
      let finalPath = href;
      if (basename && !href.startsWith(basename + '/') && href !== basename) {
        finalPath = basename + href;
      }

      // Split path / hash so we can drive the scroll ourselves post-push.
      const hashIdx = finalPath.indexOf('#');
      const finalPathOnly = hashIdx >= 0 ? finalPath.slice(0, hashIdx) : finalPath;
      const finalHash     = hashIdx >= 0 ? finalPath.slice(hashIdx)    : '';

      event.preventDefault();

      const currentFull = window.location.pathname + window.location.search + window.location.hash;
      const samePath = (window.location.pathname === finalPathOnly) || (finalPathOnly === '' && window.location.pathname === basename + '/');

      if (currentFull !== finalPath) {
        // For same-path-only-hash-changed navigations we use replaceState-like
        // behaviour but with pushState so the user can navigate back.
        window.history.pushState({}, '', finalPath);
        // React Router v6 listens for popstate, not pushState. Dispatch a
        // popstate so it picks up the new URL (unless this is a pure hash
        // change on the same path, where React Router doesn't need to do
        // anything — but dispatching is still safe and idempotent).
        window.dispatchEvent(new PopStateEvent('popstate'));
      }

      // Scroll: hash anchors are handled by scrollToHash. The element may not
      // exist yet if React just route-changed — wait one frame.
      if (finalHash) {
        requestAnimationFrame(() => requestAnimationFrame(() => scrollToHash(finalHash)));
      } else if (!samePath) {
        // New route, no hash → scroll to top like a real navigation would.
        window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
      }
    },
    { capture: true }
  );

  // First-load: if the URL we arrived on has a hash, scroll to it once React
  // has mounted and rendered the section. Browsers don't always do this
  // reliably for SPA boots.
  if (window.location.hash) {
    const initialHash = window.location.hash;
    const tryScroll = () => {
      const id = decodeURIComponent(initialHash.slice(1));
      const el = id ? document.getElementById(id) : null;
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
        return true;
      }
      return false;
    };
    // Try a few times across the first 800ms to handle lazy-rendered sections.
    let attempts = 0;
    const id = setInterval(() => {
      attempts += 1;
      if (tryScroll() || attempts >= 8) clearInterval(id);
    }, 100);
  }
}
